import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { UnreadStatusResponse } from '../types/messaging.types';

const messagingService = {
  getMessages: (
    patientPhoneNumber: string,
    clinicId: number,
    limit: number,
    skip: number,
    locationId?: number
  ) => {
    const queryParams: Record<string, any> = { limit, skip };

    if (locationId) {
      queryParams.locationId = locationId;
    }

    return ChiroUpAPI.get(
      'api',
      `/patients/${clinicId}/messaging/${encodeURIComponent(patientPhoneNumber)}`,
      {
        queryParams,
      }
    );
  },

  getConversationPatients: (patientPhoneNumber: string, clinicId: number) => {
    return ChiroUpAPI.get(
      'api',
      `/patients/${clinicId}/messaging/conversations/${encodeURIComponent(patientPhoneNumber)}/patients`,
      {}
    );
  },

  getAllConversations: async (
    clinicId: number,
    limit: number,
    skip: number,
    locationId?: number,
    twoWayTextingOnly?: boolean
  ) => {
    const queryParams: Record<string, any> = { limit, skip };

    if (locationId) {
      queryParams.locationId = locationId;
    }

    if (twoWayTextingOnly) {
      queryParams.twoWayTextingOnly = 'true';
    }

    const res = await ChiroUpAPI.get('api', `/patients/${clinicId}/messaging/conversations`, {
      queryParams,
    });
    console.log({ res });

    return res;
  },

  sendMessage: (
    data: {
      phoneNumber: string;
      patientPhoneNumber: string;
      content: string;
      locationId?: number;
      retriedMessageId?: number;
    },
    clinicId: number
  ) => {
    return ChiroUpAPI.post('api', `/patients/${clinicId}/messaging/send`, {
      body: data,
    });
  },

  getClinicPhoneNumber: (clinicId: number) => {
    return ChiroUpAPI.get('api', `/settings/${clinicId}/messaging/phone-number`, {});
  },

  searchAvailableNumbers: ({
    clinicId,
    areaCode,
    limit = 10,
  }: {
    clinicId: number;
    areaCode: string;
    limit: number;
  }) => {
    return ChiroUpAPI.post('api', `/settings/${clinicId}/messaging/search-available-numbers`, {
      body: { areaCode, limit },
    });
  },

  getAvailableAreaCodes: (clinicId: number) => {
    return ChiroUpAPI.get('api', `/settings/${clinicId}/messaging/available-area-codes`, {});
  },

  provisionPhoneNumber: (data: { clinicId: number; areaCode?: string; phoneNumber?: string }) => {
    return ChiroUpAPI.post('api', `/settings/${data.clinicId}/messaging/provision-number`, {
      body: data,
    });
  },

  getMediaSignedUrl: ({
    clinicId,
    messageId,
    hash,
    download,
  }: {
    clinicId: number;
    messageId: number;
    hash: string;
    download?: boolean;
  }) => {
    const queryParams = download ? { download: 'true' } : {};

    return ChiroUpAPI.get('api', `/patients/${clinicId}/messaging/media/${messageId}/${hash}`, {
      queryParams,
    });
  },

  // Get the unread messages status for the current clinic
  getUnreadStatus: (
    clinicId: number,
    lastCheckedTimestamp?: number,
    locationId?: number
  ): Promise<UnreadStatusResponse> => {
    console.log('getUnreadStatus', { clinicId, lastCheckedTimestamp, locationId });
    const queryParams: Record<string, string> = {};

    if (lastCheckedTimestamp) {
      queryParams.lastCheckedTimestamp = lastCheckedTimestamp.toString();
    }

    if (locationId) {
      queryParams.locationId = locationId.toString();
    }

    return ChiroUpAPI.get('api', `/patients/${clinicId}/messaging/unread-status`, { queryParams });
  },

  // Get clinic messaging status (brand, campaign, etc.)
  getClinicMessagingStatus: (clinicId: number, locationId?: number) => {
    const queryParams = locationId ? { locationId: locationId.toString() } : {};

    return ChiroUpAPI.get('api', `/messaging/${clinicId}/status`, { queryParams });
  },

  // Register messaging (brand + campaign in one call)
  registerMessaging: (clinicId: number, data: any) => {
    return ChiroUpAPI.post('api', `/messaging/${clinicId}/register`, { body: data });
  },

  // Create campaign for an existing brand (used when brand is approved later)
  createMessagingCampaign: (clinicId: number) => {
    return ChiroUpAPI.post('api', `/messaging/${clinicId}/create-campaign`, { body: {} });
  },

  // Get ALL phone numbers with brand/campaign assignments
  getAllPhoneNumbers: (clinicId: number) => {
    return ChiroUpAPI.get('api', `/messaging/${clinicId}/phone-numbers`, {});
  },

  // Assign phone number to location
  assignPhoneToLocation: (clinicId: number, phoneNumberId: number, locationId: number) => {
    return ChiroUpAPI.post('api', `/messaging/${clinicId}/assign-phone-to-location`, {
      body: { phoneNumberId, locationId },
    });
  },

  // Unassign phone number from location
  unassignPhoneFromLocation: (clinicId: number, phoneNumberId: number, locationId: number) => {
    return ChiroUpAPI.post('api', `/messaging/${clinicId}/unassign-phone-from-location`, {
      body: { phoneNumberId, locationId },
    });
  },

  // Get all brands for clinic
  getAllBrands: (clinicId: number) => {
    return ChiroUpAPI.get('api', `/messaging/${clinicId}/brands`, {});
  },

  // Assign phone number to brand/campaign
  assignPhoneToBrand: (clinicId: number, phoneNumberId: number, brandId: number) => {
    return ChiroUpAPI.post('api', `/messaging/${clinicId}/assign-phone-to-brand`, {
      body: { phoneNumberId, brandId },
    });
  },

  // Mark a conversation as unread
  markAsUnread: (
    clinicId: number,
    clinicPhoneNumberId: number,
    patientPhoneNumber: string
  ): Promise<{ success: boolean; message: string }> => {
    return ChiroUpAPI.post('api', `/patients/${clinicId}/messaging/mark-as-unread`, {
      body: { clinicPhoneNumberId, patientPhoneNumber },
    });
  },
};

export default messagingService;
